import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import type { AppRouter } from "@alm/api-contracts";
import { apiHttpOrigin } from "@/lib/env";

/** A DEVICE registration: a token naming one building and no person, sent in `x-kiosk-device`; the
 * API fixes the facility from it. Its own link, not `@/lib/orpc`, so a leaked device token never
 * rides on person-scoped requests. */

const DEVICE_KEY = "alm.kiosk.device";

export interface KioskDeviceRegistration {
  token: string;
  deviceId: string;
  deviceName: string;
  facilityId: string;
  facilityName: string;
}

export function getKioskDevice(): KioskDeviceRegistration | null {
  // Unreadable storage or a bad payload reads as no registration.
  try {
    const raw = localStorage.getItem(DEVICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KioskDeviceRegistration;
    if (!parsed?.token || !parsed?.facilityId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setKioskDevice(registration: KioskDeviceRegistration): void {
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(registration));
  } catch {
    // Private browsing: the tablet works until it is reloaded, and the setup
    // screen is where it lands after that.
  }
}

function clearKioskDevice(): void {
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Nothing to remove.
  }
}

/** Fired when the API says this registration is dead, so the screen can fall
 *  back to setup without a reload. */
export const KIOSK_DEVICE_REVOKED_EVENT = "kiosk-device-revoked";

/** The API's exact answer for a revoked, moved or deleted device. The wording
 *  is load-bearing on both sides: nothing else erases the registration. */
const DEACTIVATED_MESSAGE = "This kiosk has been deactivated.";

export function isDeviceRevoked(error: unknown): boolean {
  if (!getKioskDevice()) return false;
  const failure = error as { code?: unknown; message?: unknown } | null;
  return failure?.code === "UNAUTHORIZED" && failure?.message === DEACTIVATED_MESSAGE;
}

export function forgetRevokedDevice(): void {
  clearKioskDevice();
  window.dispatchEvent(new Event(KIOSK_DEVICE_REVOKED_EVENT));
}

const link = new RPCLink({
  url: `${apiHttpOrigin}/rpc`,
  headers: () => {
    const device = getKioskDevice();
    return device ? { "x-kiosk-device": device.token } : {};
  },
  fetch: (request, init) => globalThis.fetch(request, { ...init, credentials: "include" }),
});

const kioskClient: RouterClient<AppRouter> = createORPCClient(link);

/** Everything the tablet at the door reads and writes. */
export const kiosk = kioskClient.visitors.kiosk;

/** Registering and revoking tablets: an administrator's session only, which is
 *  why it is a different router and never sees a device token. */
export const kioskDevices = kioskClient.visitors.devices;

type Kiosk = typeof kiosk;

export type KioskState = Awaited<ReturnType<Kiosk["state"]>>;
export type OutingResidentChoice = Awaited<ReturnType<Kiosk["outingResidents"]>>[number];
export type OnSiteVisit = Awaited<ReturnType<Kiosk["onSite"]>>[number];
export type CompanyChoice = Awaited<ReturnType<Kiosk["companies"]>>[number];
export type ProviderChoice = Awaited<ReturnType<Kiosk["providers"]>>[number];
export type DepartmentChoice = Awaited<ReturnType<Kiosk["departments"]>>[number];
export type ReturningVisitor = NonNullable<Awaited<ReturnType<Kiosk["returning"]>>["visitor"]>;
export type CheckInResult = Awaited<ReturnType<Kiosk["checkIn"]>>;
export type KioskRecognition = Awaited<ReturnType<Kiosk["faces"]["recognize"]>>;
export type KioskRecognized = Exclude<
  Awaited<ReturnType<Kiosk["faces"]["answer"]>>,
  { kind: "dismissed" }
>;
export type PrintOutcome = CheckInResult["print"];

/** The API's exact answer when a phone is already on an open visit here; it carries that visit. */
const ALREADY_CHECKED_IN_MESSAGE = "This visitor is already checked in.";

/** The open visit a refused second check-in names, so the screen can offer to close it. */
export function alreadyCheckedIn(error: unknown): OnSiteVisit | null {
  const failure = error as {
    code?: unknown;
    message?: unknown;
    data?: { details?: { visit?: OnSiteVisit } };
  } | null;
  if (failure?.code !== "CONFLICT" || failure?.message !== ALREADY_CHECKED_IN_MESSAGE) return null;
  return failure.data?.details?.visit ?? null;
}

/** oRPC hands timestamps back as `Date`; anything that has been through JSON
 *  arrives as a string. Read them through `at`. */
export type Timestamp = Date | string;

const at = (value: Timestamp): number => new Date(value).getTime();

/** The tablet's clock is the building's clock: a kiosk never leaves the lobby. */
export function clockTime(value: Timestamp): string {
  return new Date(at(value)).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
